import React, { useEffect, useMemo } from 'react';
import type { MarketCouponProps } from './MarketCouponProps';
import {
  Flex,
  IconChevronDown,
  IconDownload,
  IconPointing,
} from '@banhala/ui-kit';
import { useMarketDetail } from '../../queries/useMarketDetail';
import { CouponView } from './components/CouponView';
import { isNil, localizeNumber } from 'shared/src/utils';
import { useMemberRequestsAvailability } from './queries/useMemberRequestsAvailability';
import { usePressCouponView } from './hooks/usePressCouponView';
import { useLogDisplayRequestCoupon } from './hooks/useLogDisplayRequestCoupon';
import { useMarketRoute } from '../../../../hooks/useMarketRoute';
import { ErrorBoundary } from 'shared/src/component';

const MarketCoupon: React.FC<MarketCouponProps> = props => {
  const { params } = useMarketRoute<'MarketHome'>();
  const { data: marketDetail } = useMarketDetail(props.marketId);
  const { data: memberRequestAvailable } = useMemberRequestsAvailability(
    props.marketId,
  );

  const [isShowCouponInstantly, setIsShowCouponInstantly] = React.useState(
    (params.extras?.show_coupons ?? false) || params.show_coupons === 'true',
  );

  useLogDisplayRequestCoupon(memberRequestAvailable);

  const couponViewInfo = useMemo(() => {
    if (!marketDetail) return null;

    if (memberRequestAvailable) {
      if (memberRequestAvailable.availability.is_requested) {
        return {
          title: '마켓에 쿠폰 요청 완료',
          icon: <IconPointing />,
          disabled: true,
        };
      }

      if (memberRequestAvailable.availability.is_available_request) {
        return {
          title: '마켓 찜하고 콕! 찔러 쿠폰 요청하기',
          icon: <IconPointing />,
        };
      }
    }

    if (marketDetail.max_price_coupon) {
      if (marketDetail.max_price_coupon.discount_type === '0') {
        return {
          title: `최대 ${localizeNumber(marketDetail.max_price_coupon.discount_price ?? 0)}원 할인 쿠폰 받기`,
          icon: <IconDownload />,
          disabled: false,
        };
      }

      if (marketDetail.max_price_coupon.discount_type === '1') {
        return {
          title: `최대 ${marketDetail.max_price_coupon.discount_percent ?? 0}% 할인 쿠폰 받기`,
          icon: <IconDownload />,
          disabled: false,
        };
      }
    }

    if (marketDetail.market.is_exist_usable_coupon) {
      return {
        title: '내가 받은 쿠폰 확인하기',
        icon: <IconChevronDown />,
      };
    }

    return null;
  }, [marketDetail, memberRequestAvailable]);

  const onPressCouponView = usePressCouponView(props.marketId);

  useEffect(() => {
    if (marketDetail && couponViewInfo) {
      if (isShowCouponInstantly) {
        if (marketDetail.max_price_coupon && memberRequestAvailable != null) {
          onPressCouponView.action();
          setIsShowCouponInstantly(false);
        }
      }
    }
  }, [
    couponViewInfo,
    isShowCouponInstantly,
    marketDetail,
    memberRequestAvailable,
    onPressCouponView,
  ]);

  if (isNil(couponViewInfo)) {
    return null;
  }

  return (
    <Flex backgroundColor={'bg_secondary'}>
      <Flex margin={{ horizontal: 16, vertical: 16 }}>
        <CouponView
          title={couponViewInfo.title}
          icon={couponViewInfo.icon}
          disabled={couponViewInfo.disabled}
          loading={onPressCouponView.loading}
          onPress={onPressCouponView.action}
        />
      </Flex>
    </Flex>
  );
};

const MarketCouponWrapper: React.FC<MarketCouponProps> = props => {
  return (
    // 에러 시 쿠폰 안 보여주기
    <ErrorBoundary ErrorFallback={() => <></>}>
      <React.Suspense fallback={<Flex backgroundColor={'bg_secondary'}></Flex>}>
        <MarketCoupon {...props} />
      </React.Suspense>
    </ErrorBoundary>
  );
};

export default MarketCouponWrapper;
